import React from 'react';
import { Building2, Users, MapPin, CheckCircle, Play, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';

const CorporateSection = () => {
  const benefits = [
    "We come to you with mobile massage tables",
    "Chair massage available for quick sessions",
    "Reduce workplace stress and tension",
    "Boost employee morale and productivity",
    "Flexible on-site or venue arrangements"
  ];

  const tiktokVideos = [
    {
      id: 1,
      url: "https://vm.tiktok.com/ZNRhnjH32/",
      title: "Corporate Event at Cloughjordan House"
    },
    {
      id: 2,
      url: "https://vm.tiktok.com/ZNRhnRfEH/",
      title: "Deloitte Corporate Massage"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-sage-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-warm-500/10 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Building2 size={18} />
              <span>Corporate Services</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
              Corporate
              <span className="block text-sage-400">Massage Services</span>
            </h2>
            
            <p className="text-stone-300 text-lg mb-6 leading-relaxed">
              Bring the healing power of massage to your workplace! We provide professional 
              on-site massage services for corporate events, team wellness days, and employee 
              appreciation programs. <strong className="text-white">We come directly to you</strong> with 
              our mobile massage tables and chair massage tables - no setup hassle for you!
            </p>

            {/* Recent Client Highlight */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-sage-500 rounded-full flex items-center justify-center">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <p className="font-semibold">Recent Corporate Event</p>
                  <p className="text-sm text-stone-400">Trusted by leading companies</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-lg font-medium text-sage-300">Deloitte</p>
                  <p className="text-sm text-stone-400 flex items-center gap-1">
                    <MapPin size={14} />
                    Cloughjordan House
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-3 mb-8">
              {benefits.map((benefit) => (
                <div key={benefit} className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-sage-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={14} />
                  </div>
                  <span className="text-stone-200">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => window.open(`https://wa.me/${businessInfo.whatsapp}?text=Hi! I'm interested in corporate massage services for my company.`, '_blank')}
                className="bg-sage-600 hover:bg-sage-500 text-white px-8 py-4 rounded-full transition-all duration-300 group"
              >
                Enquire for Your Company
                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => window.open(`mailto:${businessInfo.email}?subject=Corporate Massage Enquiry`, '_blank')}
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-full"
              >
                Email Us
              </Button>
            </div>
          </div>

          {/* TikTok Videos */}
          <div className="space-y-6">
            <p className="text-stone-400 text-sm font-medium mb-4">Watch our corporate events:</p>
            <div className="grid gap-6">
              {tiktokVideos.map((video) => (
                <a
                  key={video.id}
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-br from-stone-800 to-stone-700 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/10 hover:border-sage-500/50"
                >
                  <div className="aspect-video relative flex items-center justify-center bg-stone-900/50">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="relative z-10 text-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-3 flex items-center justify-center group-hover:scale-110 group-hover:bg-sage-500/50 transition-all">
                        <Play size={28} className="text-white ml-1" />
                      </div>
                      <p className="text-white font-medium">{video.title}</p>
                      <p className="text-white/60 text-sm mt-1">Watch on TikTok</p>
                    </div>
                    
                    {/* TikTok Logo */}
                    <div className="absolute bottom-4 right-4 flex items-center gap-2 text-white/70">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                      </svg>
                      <span className="text-sm">@dublin.holistic.m</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* Trust Badge */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-stone-300">
                <Users size={20} className="text-sage-400" />
                <span>Trusted by corporate clients across Ireland</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CorporateSection;
